import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { card } from "./common";

const AUTO = "__auto__";
const MODES = { both: "Pickup & drop", pickup: "Pickup only", drop: "Drop only" };

function Sel({ label, value, onChange, items, testid, extra }) {
  return (
    <div><Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1"><SelectValue placeholder="Choose" /></SelectTrigger>
        <SelectContent>{extra}{items.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export function AssignPanel({ branchId, canEdit }) {
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ student_id: "", route_id: "", stop_id: "", vehicle_id: AUTO, mode: "both" });
  const load = () => branchId && api.get("/transport/assignments", { params: { branch_id: branchId } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    if (!branchId) return;
    load();
    api.get("/transport/routes", { params: { branch_id: branchId } }).then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/transport/vehicles", { params: { branch_id: branchId } }).then((r) => setVehicles(r.data.map((v) => ({ id: v.id, name: v.reg_no })))).catch(() => {});
    if (canEdit) api.get("/students", { params: { branch_id: branchId } }).then((r) => setStudents((r.data.items || r.data).map((s) => ({ id: s.id, name: `${s.name} (${s.class_name}-${s.section})` })))).catch(() => {});
  }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const route = routes.find((r) => r.id === f.route_id);
  const rname = Object.fromEntries(routes.map((r) => [r.id, r]));
  const vname = Object.fromEntries(vehicles.map((v) => [v.id, v.name]));
  async function save() {
    try { await api.post("/transport/assignments", { ...f, vehicle_id: f.vehicle_id === AUTO ? null : f.vehicle_id }); toast.success("Assigned — parent notified"); setF({ ...f, student_id: "" }); load(); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function del(a) { if (!window.confirm(`Remove ${a.student.name} from transport?`)) return; try { await api.delete(`/transport/assignments/${a.student_id}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  const shown = rows.filter((a) => a.student.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-5">
      {canEdit && (
        <div data-testid="assign-form" className={`${card} space-y-3`}>
          <h3 className="font-display font-bold text-slate-900">Assign a student to a route</h3>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Sel label="Student" value={f.student_id} onChange={(v) => setF({ ...f, student_id: v })} items={students} testid="assign-student" />
            <Sel label="Route" value={f.route_id} onChange={(v) => setF({ ...f, route_id: v, stop_id: "" })} items={routes} testid="assign-route" />
            <Sel label="Stop" value={f.stop_id} onChange={(v) => setF({ ...f, stop_id: v })} items={route?.stops || []} testid="assign-stop" />
            <Sel label="Vehicle" value={f.vehicle_id} onChange={(v) => setF({ ...f, vehicle_id: v })} items={vehicles} testid="assign-vehicle" extra={<SelectItem value={AUTO}>Bus on this route</SelectItem>} />
            <Sel label="Uses" value={f.mode} onChange={(v) => setF({ ...f, mode: v })} items={Object.entries(MODES).map(([id, name]) => ({ id, name }))} testid="assign-mode" />
          </div>
          <Button data-testid="assign-save" className="btn-brand" onClick={save} disabled={!f.student_id || !f.route_id || !f.stop_id}>Assign</Button>
        </div>
      )}
      <Input data-testid="assign-search" placeholder="Search students" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-white" />
      <div className={card}>
        {shown.length === 0 ? <div className="text-sm text-slate-500">No students assigned yet.</div> : shown.map((a) => {
          const r = rname[a.route_id];
          return (
            <div key={a.student_id} data-testid={`assign-${a.student_id}`} className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100 last:border-0 text-sm">
              <span className="font-semibold">{a.student.name}</span><span className="text-xs text-slate-400">{a.student.class_name}-{a.student.section}</span>
              <span className="text-slate-600">{r?.name} • {r?.stops.find((s) => s.id === a.stop_id)?.name}</span>
              <Badge className="bg-violet-100 text-violet-800">{vname[a.vehicle_id] || "No bus"}</Badge><Badge className="bg-slate-100 text-slate-600">{MODES[a.mode]}</Badge>
              {canEdit && <button data-testid={`assign-delete-${a.student_id}`} onClick={() => del(a)} className="ml-auto text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
